import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, FolderPlus, User, Users, Link2, FileUp, PieChart, ChevronUp } from 'lucide-react';
import { useOnboardingStore } from '../../stores/useOnboardingStore.ts';
import { useTranslation } from '../../i18n.ts';

interface GuideStep {
  selector: string;
  delay: number;
}

interface ChecklistItemDef {
  key: string;
  i18nKey: string;
  icon: React.ReactNode;
  route: string | null;
  guideSteps?: GuideStep[];
}

const CHECKLIST_ITEMS: ChecklistItemDef[] = [
  {
    key: 'projectCreated',
    i18nKey: 'onboarding_checklist_projectCreated',
    icon: <FolderPlus size={16} />,
    route: null,
    guideSteps: [
      { selector: '[data-tour="project-switcher"]', delay: 0 },
      { selector: '[data-tour="new-project-btn"]', delay: 0 },
    ],
  },
  {
    key: 'probandSet',
    i18nKey: 'onboarding_checklist_probandSet',
    icon: <User size={16} />,
    route: '/tree',
    guideSteps: [
      { selector: '[data-tour="tree-link"]', delay: 0 },
      { selector: '[data-tour="set-proband-btn"]', delay: 0 },
    ],
  },
  {
    key: 'fivePersonsAdded',
    i18nKey: 'onboarding_checklist_fivePersonsAdded',
    icon: <Users size={16} />,
    route: '/people',
    guideSteps: [
      { selector: '[data-tour="people-link"]', delay: 0 },
      { selector: '[data-tour="add-person"]', delay: 0 },
    ],
  },
  {
    key: 'sourceLinked',
    i18nKey: 'onboarding_checklist_sourceLinked',
    icon: <Link2 size={16} />,
    route: '/sources',
    guideSteps: [{ selector: '[data-tour="sources-link"]', delay: 0 }],
  },
  {
    key: 'gedcomImported',
    i18nKey: 'onboarding_checklist_gedcomImported',
    icon: <FileUp size={16} />,
    route: '/import',
  },
  {
    key: 'fanChartViewed',
    i18nKey: 'onboarding_checklist_fanChartViewed',
    icon: <PieChart size={16} />,
    route: '/tree',
    guideSteps: [{ selector: '[data-tour="tree-link"]', delay: 0 }],
  },
];

// ── Glow helpers ──

function clearAllGlows() {
  document.querySelectorAll('.onboarding-glow').forEach((el) => {
    el.classList.remove('onboarding-glow');
  });
}

function startGlow(selector: string) {
  clearAllGlows();
  const el = document.querySelector(selector);
  if (!el) return;

  el.classList.add('onboarding-glow');
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

  // Remove glow on click
  const handler = () => {
    el.classList.remove('onboarding-glow');
    el.removeEventListener('click', handler);
  };
  el.addEventListener('click', handler, { once: true });
}

// ── Component ──

const OnboardingChecklist: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { state, showChecklist, isChecklistExpanded, dismissChecklist, toggleChecklistExpanded } =
    useOnboardingStore();

  useEffect(() => {
    if (!isChecklistExpanded) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') toggleChecklistExpanded();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isChecklistExpanded, toggleChecklistExpanded]);

  const checklist = state.checklist || {};
  const completedCount = CHECKLIST_ITEMS.filter(
    (item) => checklist[item.key as keyof typeof checklist]
  ).length;
  const totalCount = CHECKLIST_ITEMS.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);
  const allDone = completedCount === totalCount;

  // Find the first incomplete item for the golden highlight
  const nextIncompleteKey = CHECKLIST_ITEMS.find(
    (item) => !checklist[item.key as keyof typeof checklist]
  )?.key;

  const handleItemClick = useCallback(
    (item: ChecklistItemDef) => {
      const isCompleted = !!checklist[item.key as keyof typeof checklist];
      if (isCompleted) return;

      // Navigate to the route first (if defined)
      if (item.route) {
        navigate(item.route);
      }

      const steps = item.guideSteps;
      if (steps?.length) {
        // Start guided glow after short delay so the target page is rendered
        setTimeout(() => startGlow(steps[0].selector), 500);
        // Collapse checklist so it doesn't obstruct
        if (isChecklistExpanded) {
          toggleChecklistExpanded();
        }
      }
    },
    [checklist, isChecklistExpanded, toggleChecklistExpanded, navigate]
  );

  if (!showChecklist) return null;

  // ── Collapsed state: pill ──
  if (!isChecklistExpanded) {
    return (
      <button
        onClick={toggleChecklistExpanded}
        className={`
          fixed bottom-6 right-6 z-40
          flex items-center gap-2.5 px-4 py-2.5
          bg-pt-forest dark:bg-[#1A2F23] text-pt-gold
          rounded-full shadow-lg hover:shadow-xl
          border border-pt-gold/20
          transition-all duration-300 hover:scale-105
          ${!allDone ? 'animate-[onboarding-pill-pulse_3s_ease-in-out_infinite]' : ''}
        `}
        title={t('onboarding_checklist_title')}
      >
        {/* Circular SVG progress ring */}
        <svg width="28" height="28" viewBox="0 0 28 28" className="flex-shrink-0">
          {/* Background ring */}
          <circle
            cx="14"
            cy="14"
            r="11"
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.15}
            strokeWidth={2.5}
          />
          {/* Progress ring */}
          <circle
            cx="14"
            cy="14"
            r="11"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeDasharray={`${(progressPercent / 100) * 69.115} 69.115`}
            transform="rotate(-90 14 14)"
            className="transition-all duration-500"
          />
          {/* Count text */}
          <text
            x="14"
            y="14"
            textAnchor="middle"
            dominantBaseline="central"
            fill="currentColor"
            fontSize="9"
            fontWeight="800"
          >
            {completedCount}/{totalCount}
          </text>
        </svg>
        <span className="text-xs font-bold tracking-wide">{t('onboarding_checklist_title')}</span>
      </button>
    );
  }

  // ── Expanded state: floating card ──
  return (
    <div
      className="
        fixed bottom-20 right-6 z-40 w-72
        bg-white dark:bg-[#1A2F23]
        rounded-2xl shadow-2xl
        border border-pt-gold/20 dark:border-pt-gold/15
        transition-all duration-300
        animate-[onboarding-expand_0.3s_ease-out]
        max-h-[calc(100vh-200px)] overflow-y-auto
      "
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <h3 className="text-base font-bold text-pt-forest dark:text-pt-cream">
          {t('onboarding_checklist_title')}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleChecklistExpanded}
            className="p-1.5 rounded-lg text-pt-sage hover:text-pt-forest hover:bg-pt-cream/60 dark:hover:text-pt-cream dark:hover:bg-white/10 transition-colors"
            title={t('onboarding_checklist_minimize')}
          >
            <ChevronUp size={14} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5 pb-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-pt-sage dark:text-pt-sage/80">
            {t('onboarding_checklist_progress', {
              done: String(completedCount),
              total: String(totalCount),
            })}
          </span>
          <span className="text-xs font-semibold text-pt-gold">{progressPercent}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-pt-cream dark:bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full bg-pt-gold transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Checklist items */}
      <ul className="px-3 pb-2 space-y-0.5 max-h-[320px] overflow-y-auto">
        {CHECKLIST_ITEMS.map((item) => {
          const isCompleted = !!checklist[item.key as keyof typeof checklist];
          const isNext = item.key === nextIncompleteKey;
          const isClickable = !isCompleted;

          return (
            <li key={item.key}>
              <button
                onClick={() => handleItemClick(item)}
                disabled={!isClickable}
                className={`
                  w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left text-sm transition-all
                  ${isNext ? 'border-l-2 border-pt-gold bg-pt-gold/5 dark:bg-pt-gold/10' : 'border-l-2 border-transparent'}
                  ${
                    isCompleted
                      ? 'text-pt-sage line-through dark:text-pt-sage/60'
                      : 'text-pt-forest dark:text-pt-cream'
                  }
                  ${isClickable ? 'hover:bg-pt-cream/60 dark:hover:bg-white/5 cursor-pointer' : ''}
                  ${isCompleted ? 'cursor-default' : ''}
                `}
              >
                {/* Check circle */}
                <span
                  className={`
                    flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full border transition-colors
                    ${
                      isCompleted
                        ? 'bg-pt-gold border-pt-gold text-[var(--forest)]'
                        : 'border-pt-sage/40 dark:border-pt-sage/30'
                    }
                  `}
                >
                  {isCompleted && <Check size={12} strokeWidth={3} />}
                </span>

                {/* Icon */}
                <span
                  className={`flex-shrink-0 ${isCompleted ? 'text-pt-sage dark:text-pt-sage/60' : 'text-pt-gold'}`}
                >
                  {item.icon}
                </span>

                {/* Label */}
                <span className="flex-1">{t(item.i18nKey)}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-pt-forest/5 dark:border-white/5">
        <button
          onClick={dismissChecklist}
          className="text-xs text-pt-sage hover:text-pt-forest dark:hover:text-pt-cream transition-colors"
        >
          {t('onboarding_checklist_hide')}
        </button>
      </div>
    </div>
  );
};

export default OnboardingChecklist;
